// Reports Page (FRA - Financial Reporting & Analysis)

import { Link, useNavigate } from 'react-router-dom';
import { trpc } from '../../lib/trpc';
import { useAuthStore } from '../../stores/auth-store';
import { ProductUpsellBanner } from '../../components/ProductUpsellBanner';

// ERP type display configuration
type ERPType = 'odoo' | 'acumatica' | 'quickbooks' | 'sage' | 'netsuite' | 'xero';

const ERP_DISPLAY: Record<ERPType, { name: string; logo: string }> = {
  odoo: { name: 'Odoo', logo: '/assets/erp/odoo.png' },
  acumatica: { name: 'Acumatica', logo: '/assets/erp/acumatica.png' },
  quickbooks: { name: 'QuickBooks', logo: '/assets/erp/quickbooks.png' },
  sage: { name: 'Sage', logo: '/assets/erp/sage.svg' },
  netsuite: { name: 'NetSuite', logo: '/assets/erp/netsuite.svg' },
  xero: { name: 'Xero', logo: '/assets/erp/xero.svg' },
};

function getErpDisplay(erpType: string): { name: string; logo: string } {
  return ERP_DISPLAY[erpType as ERPType] ?? { name: erpType, logo: '/assets/erp/odoo.png' };
}

const FRA_UPSELL_FEATURES = [
  'Generate trial balance, income statement, and balance sheet reports',
  'Advanced cash flow and variance analysis',
  'Consolidated multi-company financial reports',
  'Live ERP data with automated refresh',
  'Excel-based reporting with CLM functions',
];

export function ReportsPage() {
  const navigate = useNavigate();
  const { data: tenant } = trpc.tenant.getCurrent.useQuery();
  const { data: connections, isLoading: connectionsLoading } = trpc.erp.listConnections.useQuery();
  const { user } = useAuthStore();

  // Check if user has FRA access
  const hasFraAccess = tenant?.subscription?.products?.some((p: { id: string }) => p.id === 'fra');
  const isAdmin = user?.role === 'tenant_admin';

  // Show upsell if tenant data loaded and no FRA access
  if (tenant && !hasFraAccess) {
    return (
      <ProductUpsellBanner
        productId="fra"
        productName="Financial Reporting & Analysis"
        features={FRA_UPSELL_FEATURES}
        isAdmin={isAdmin ?? false}
      />
    );
  }

  const hasAdvancedReporting = tenant?.features?.fraAdvancedReporting;
  const hasConsolidatedReports = tenant?.features?.fraConsolidatedReports;

  const hasConnections = connections && connections.length > 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Financial Reports</h1>
          <p className="text-gray-600">Access and generate financial reports from your ERP data</p>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/reports/designer"
            className="btn-secondary"
          >
            Design Report Template
          </Link>
          <button
            className="btn-primary"
            disabled={!hasFraAccess}
          >
            Create Report
          </button>
        </div>
      </div>

      {/* ERP Connections or Warning */}
      {connectionsLoading ? (
        <div className="card">
          <div className="flex items-center justify-center py-4">
            <svg className="animate-spin h-5 w-5 text-gray-400 mr-2" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            <span className="text-sm text-gray-500">Loading connections...</span>
          </div>
        </div>
      ) : hasConnections ? (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">ERP Connections</h2>
            <Link
              to="/erp"
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              Manage Connections
            </Link>
          </div>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {connections.map((conn: { id: string; name: string; erpType: string; status: string; url: string; serverVersion: string | null }) => {
              const display = getErpDisplay(conn.erpType);
              return (
                <div key={conn.id} className="bg-white border border-gray-200 rounded-xl p-5 hover:border-gray-300 transition-colors">
                  <div className="flex flex-col items-center mb-3">
                    <div className="h-16 flex items-center justify-center mb-3">
                      <img src={display.logo} alt={display.name} className="max-h-full object-contain" style={{ width: '140px' }} />
                    </div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-gray-900">{conn.name}</h3>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        conn.status === 'active'
                          ? 'bg-green-100 text-green-700'
                          : conn.status === 'error'
                          ? 'bg-red-100 text-red-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {conn.status === 'active' ? 'Connected' : conn.status === 'error' ? 'Error' : 'Disconnected'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500">{display.name} {conn.serverVersion || ''}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="card bg-amber-50 border-amber-200">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <svg className="w-5 h-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-amber-900">No ERP connection</h3>
              <p className="text-sm text-amber-700">
                Connect your ERP system to start generating financial reports
              </p>
            </div>
            <Link
              to="/erp"
              className="btn-secondary text-amber-700 border-amber-300 hover:bg-amber-100"
            >
              Connect ERP
            </Link>
          </div>
        </div>
      )}

      {/* Report Types */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <ReportCard
          title="Trial Balance"
          description="View account balances for a specific period"
          icon={
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
          }
          available={hasFraAccess}
          onClick={() => navigate('/reports/designer?preset=trial_balance')}
        />
        <ReportCard
          title="Income Statement"
          description="Profit and loss for selected periods"
          icon={
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          }
          available={hasFraAccess}
          onClick={() => navigate('/reports/designer?preset=income_statement')}
        />
        <ReportCard
          title="Balance Sheet"
          description="Assets, liabilities, and equity snapshot"
          icon={
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
            </svg>
          }
          available={hasFraAccess}
          onClick={() => navigate('/reports/designer?preset=balance_sheet')}
        />
        <ReportCard
          title="Cash Flow"
          description="Operating, investing, and financing activities"
          icon={
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
          available={hasAdvancedReporting}
          badge={!hasAdvancedReporting ? 'Premium' : undefined}
          onClick={() => navigate('/reports/designer?preset=cash_flow')}
        />
        <ReportCard
          title="Variance Analysis"
          description="Compare actuals vs budget with explanations"
          icon={
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
            </svg>
          }
          available={hasAdvancedReporting}
          badge={!hasAdvancedReporting ? 'Premium' : undefined}
          onClick={() => navigate('/reports/designer?preset=variance_analysis')}
        />
        <ReportCard
          title="Consolidated Reports"
          description="Multi-company combined financials"
          icon={
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          }
          available={hasConsolidatedReports}
          badge={!hasConsolidatedReports ? 'Elite' : undefined}
          onClick={() => navigate('/reports/designer?preset=consolidated')}
        />
      </div>

      {/* Saved Report Templates */}
      <SavedTemplatesSection />

      {/* Excel Integration Promo */}
      <div className="card bg-primary-50 border-primary-200">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
            <svg className="w-5 h-5 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
            </svg>
          </div>
          <div className="flex-1">
            <h3 className="font-medium text-primary-900">Access reports directly in Excel</h3>
            <p className="text-sm text-primary-700">
              Use the Calculom Excel Add-in for live data and custom formulas
            </p>
          </div>
          <a
            href="https://docs.calculom.com/excel-addin"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary text-primary-700 border-primary-300 hover:bg-primary-100"
          >
            Learn More
          </a>
        </div>
      </div>
    </div>
  );
}

function ReportCard({
  title,
  description,
  icon,
  available,
  badge,
  onClick,
}: {
  title: string;
  description: string;
  icon: React.ReactNode;
  available?: boolean;
  badge?: string;
  onClick?: () => void;
}) {
  return (
    <button
      className={`text-left border rounded-lg p-4 transition-colors ${
        available
          ? 'border-gray-200 hover:border-primary-300 hover:bg-primary-50'
          : 'border-gray-200 bg-gray-50 opacity-75 cursor-not-allowed'
      }`}
      disabled={!available}
      onClick={available ? onClick : undefined}
    >
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
          available ? 'bg-primary-100 text-primary-600' : 'bg-gray-200 text-gray-400'
        }`}>
          {icon}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-medium text-gray-900">{title}</h3>
            {badge && (
              <span className="text-xs bg-primary-100 text-primary-700 px-2 py-0.5 rounded-full">
                {badge}
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500 mt-1">{description}</p>
        </div>
      </div>
    </button>
  );
}

function SavedTemplatesSection() {
  const navigate = useNavigate();
  const { data: templates, isLoading, error } = trpc.fra.listTemplates.useQuery({ limit: 50 });
  const deleteTemplate = trpc.fra.deleteTemplate.useMutation();
  const utils = trpc.useUtils();

  const handleDelete = async (id: string) => {
    try {
      await deleteTemplate.mutateAsync({ id });
      await utils.fra.listTemplates.invalidate();
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  if (isLoading) {
    return (
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Saved Report Templates</h2>
        <div className="text-center py-4 text-sm text-gray-500">Loading templates...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Saved Report Templates</h2>
        <div className="text-center py-4 text-sm text-red-500">
          Failed to load templates: {error.message}
        </div>
      </div>
    );
  }

  if (!templates || templates.length === 0) {
    return (
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Saved Report Templates</h2>
        <div className="text-center py-8 text-gray-500">
          <svg className="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p className="font-medium">No saved templates yet</p>
          <p className="text-sm">Design a report template and save it to see it here</p>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Saved Report Templates</h2>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 text-left">
              <th className="pb-2 font-medium text-gray-600">Name</th>
              <th className="pb-2 font-medium text-gray-600">Type</th>
              <th className="pb-2 font-medium text-gray-600">Last Updated</th>
              <th className="pb-2 font-medium text-gray-600 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {templates.map((t) => (
              <tr key={t.id} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="py-2.5 font-medium text-gray-900">{t.name}</td>
                <td className="py-2.5 text-gray-600 capitalize">{t.category?.replace(/_/g, ' ') ?? 'Custom'}</td>
                <td className="py-2.5 text-gray-500">
                  {new Date(t.updatedAt).toLocaleDateString()}
                </td>
                <td className="py-2.5 text-right">
                  <button
                    onClick={() => navigate(`/reports/designer/${t.id}`)}
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium mr-3"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(t.id)}
                    className="text-sm text-red-500 hover:text-red-700 font-medium"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
